import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, Edit2, Save, X, Search, FlaskConical, RotateCcw, Maximize2, Sparkles, Loader2 } from 'lucide-react';
import { suggestMathSymbol } from '../services/geminiService';
import { parseMarkdown } from '../utils/markdownUtils';
import {
    getAllSymbols,
    getAllPatterns,
    addCustomSymbol,
    updateCustomSymbol,
    deleteCustomSymbol,
    addCustomPattern,
    updateCustomPattern,
    deleteCustomPattern,
    toggleCustomPattern,
    togglePattern,
    disableAllPatterns,
    enableAllPatterns,
    refreshSymbolCache,
    refreshPatternsFromFirebase,
    setCurrentUser,  // ADD: Need to re-set user when modal opens
    MathSymbol,
    MathPattern
} from '../utils/mathSymbols';

// Helper for preview
const LatexPreview = ({ latex }: { latex: string }) => {
    const html = React.useMemo(() => {
        try {
            // @ts-ignore
            if (typeof window !== 'undefined' && window.katex) {
                // @ts-ignore
                return window.katex.renderToString(latex, { throwOnError: false, displayMode: false });
            }
        } catch (e) {
            console.error(e);
        }
        return null;
    }, [latex]);

    if (!html) return <span className="text-sm font-mono text-gray-500">{latex}</span>;
    return <span dangerouslySetInnerHTML={{ __html: html }} className="text-lg" />;
};

// Helper: generate a concrete example input from a pattern definition.
// mathZoneOnly patterns are wrapped in $...$ to show users they must use dollar signs.
const getExampleInput = (pattern: MathPattern): string => {
    const raw = pattern.pattern.replace(/\{([^}]+)\}/g, (_, name) => name[0]);
    return pattern.mathZoneOnly ? `$${raw}$` : raw;
};

// Comprehensive self-demonstrating note.
// Edit mode → user sees the syntax. View mode → syntax renders into math.
// Toggling a pattern below updates the View output instantly.
const DEMO_TEXT = `# Hướng dẫn gõ tắt toán học

Bấm **Xem** để thấy kết quả. Bật/tắt pattern bên dưới → demo tự cập nhật.

---

## ✅ Gõ tự do — hoạt động ở mọi nơi

**Số mũ, chỉ số, căn, làm tròn**
\`x^2\` → x^2 | \`x_i\` → x_i | \`sqrt(x)\` → sqrt(x) | \`floor(x)\` → floor(x) | \`ceil(x)\` → ceil(x)

**Chuẩn & tích vô hướng**
\`||x||\` → ||x|| | \`<u,v>\` → <u,v>

**Vector**
\`v_vec\` → v_vec | \`n_hat\` → n_hat | \`x_bar\` → x_bar | \`x_tilde\` → x_tilde | \`x_dot\` → x_dot | \`x_ddot\` → x_ddot | \`x_bold\` → x_bold

**Ma trận — tích chuyển vị, nghịch đảo**
\`ATB\` → ATB | \`Ainv\` → Ainv | \`AinvT\` → AinvT

**Ma trận — det, trace, rank, identity**
\`det(A)\` → det(A) | \`tr(A)\` → tr(A) | \`rank(A)\` → rank(A) | \`I_n\` → I_n

**Ma trận — Hessian, Jacobian, Kronecker, Hadamard**
\`H_f\` → H_f | \`J_f\` → J_f | \`A⊗B\` → A⊗B | \`A∘B\` → A∘B

**Giải tích — giới hạn, đạo hàm, tích phân**
\`lim_x->0\` → lim_x->0 | \`d/dx\` → d/dx | \`∂L/∂w\` → ∂L/∂w | \`∇^2\` → ∇^2
\`∫_0^1\` → ∫_0^1 | \`div(F)\` → div(F) | \`curl(F)\` → curl(F)

**Tổng & tích**
\`Σ_i=1:n\` → Σ_i=1:n | \`∑_i=1:n\` → ∑_i=1:n | \`Π_k=1:n\` → Π_k=1:n | \`∏_k=1:n\` → ∏_k=1:n

**Xác suất & thống kê**
\`E[X]\` → E[X] | \`Var(X)\` → Var(X) | \`P(A|B)\` → P(A|B) | \`C(n,k)\` → C(n,k)
\`Cov(X,Y)\` → Cov(X,Y) | \`Corr(X,Y)\` → Corr(X,Y) | \`N(mu,sigma)\` → N(mu,sigma)

**Tối ưu & độ phức tạp**
\`argmin_x\` → argmin_x | \`argmax_x\` → argmax_x | \`x*\` → x* | \`O(n)\` → O(n) | \`Θ(n)\` → Θ(n)

**Font & tập hợp**
\`cal_L\` → cal_L | \`bb_R\` → bb_R | \`A⊆B\` → A⊆B

---

## ⚠️ Phải bọc trong $...$ mới convert

Các cú pháp này có thể trigger nhầm trong văn bản thường, nên BẮT BUỘC dùng $...$.

**Chuyển vị** — gõ tự do KHÔNG convert: AT | bọc trong $ CÓ convert: $AT$

**Phân số** — gõ tự do: a/b | bọc trong $: $a/b$

**Kéo theo & tương đương** — $A => B$ | $P <=> Q$

**Xấp xỉ & tỉ lệ** — $x ~= y$ | $y prop x$

**Thuộc tập** — $xinR$ | $xinS$

---

## Công thức dài: dùng $$...$$

$$w = w - alpha * nabla L(w)$$

$$x = Ainv * b, ||b|| > 0$$

$$Σ_i=1:n x_i = n * x_bar$$

$$AT * A * Ainv = AT$$`;

export const MathSymbolsSettings: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'symbols' | 'patterns'>('symbols');
    const [symbols, setSymbols] = useState<MathSymbol[]>([]);
    const [patterns, setPatterns] = useState<MathPattern[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [editingSymbol, setEditingSymbol] = useState<string | null>(null);
    const [editingPattern, setEditingPattern] = useState<string | null>(null);
    const [isAddingSymbol, setIsAddingSymbol] = useState(false);
    const [isAddingPattern, setIsAddingPattern] = useState(false);

    const [aiSymbolDesc, setAiSymbolDesc] = useState('');
    const [aiSymbolLoading, setAiSymbolLoading] = useState(false);
    const [aiSymbolError, setAiSymbolError] = useState('');

    const [symbolForm, setSymbolForm] = useState({
        trigger: '',
        unicode: '',
        latex: '',
        description: '',
        category: 'Custom'
    });

    const [patternForm, setPatternForm] = useState({
        pattern: '',
        latex: '',
        description: '',
        priority: 10
    });

    const [patternTestInput, setPatternTestInput] = useState('');
    const [showMismatchExplanation, setShowMismatchExplanation] = useState(false);
    const [testingPatternId, setTestingPatternId] = useState<string | null>(null);
    const [inlineTestInput, setInlineTestInput] = useState('');
    const [demoMode, setDemoMode] = useState<'edit' | 'view'>('edit');
    const [demoText, setDemoText] = useState(DEMO_TEXT);
    const [playgroundOpen, setPlaygroundOpen] = useState(false);
    const demoRendered = useMemo(() => parseMarkdown(demoText), [demoText, patterns]);

    useEffect(() => {
        const refreshData = async () => {
            // Get current user from Firebase auth
            const { auth } = await import('../services/firebase');
            const currentUser = auth.currentUser;

            if (currentUser) {
                // CRITICAL: Re-set currentUserId so sync works when toggling
                setCurrentUser(currentUser.uid);

                // Refresh from Firebase first (pull latest changes from other devices)
                await refreshPatternsFromFirebase(currentUser.uid);
            }

            // Then load into UI
            loadData();
        };

        refreshData();
    }, []);

    const loadData = () => {
        setSymbols(getAllSymbols());
        setPatterns(getAllPatterns());
    };

    const handleAddSymbol = () => {
        if (!symbolForm.trigger || !symbolForm.latex) return;
        addCustomSymbol({
            trigger: symbolForm.trigger.split(',').map(t => t.trim()),
            unicode: symbolForm.unicode,
            latex: symbolForm.latex,
            description: symbolForm.description,
            category: symbolForm.category
        });
        refreshSymbolCache();
        loadData();
        setIsAddingSymbol(false);
        resetSymbolForm();
    };

    const handleUpdateSymbol = (id: string) => {
        if (!symbolForm.trigger || !symbolForm.latex) return;
        updateCustomSymbol(id, {
            trigger: symbolForm.trigger.split(',').map(t => t.trim()),
            unicode: symbolForm.unicode,
            latex: symbolForm.latex,
            description: symbolForm.description,
            category: symbolForm.category
        });
        refreshSymbolCache();
        loadData();
        setEditingSymbol(null);
        resetSymbolForm();
    };

    const handleDeleteSymbol = (id: string) => {
        if (confirm('Delete this custom symbol?')) {
            deleteCustomSymbol(id);
            refreshSymbolCache();
            loadData();
        }
    };

    const handleAddPattern = () => {
        if (!patternForm.pattern || !patternForm.latex) return;
        addCustomPattern({
            pattern: patternForm.pattern,
            latex: patternForm.latex,
            description: patternForm.description,
            priority: patternForm.priority
        });
        loadData();
        setIsAddingPattern(false);
        resetPatternForm();
    };

    const handleUpdatePattern = (id: string) => {
        if (!patternForm.pattern || !patternForm.latex) return;
        updateCustomPattern(id, {
            pattern: patternForm.pattern,
            latex: patternForm.latex,
            description: patternForm.description,
            priority: patternForm.priority
        });
        loadData();
        setEditingPattern(null);
        resetPatternForm();
    };

    const handleDeletePattern = (id: string) => {
        if (confirm('Delete this custom pattern?')) {
            deleteCustomPattern(id);
            loadData();
        }
    };

    const handleTogglePattern = (pattern: MathPattern) => {
        togglePattern(pattern.pattern, pattern.id);
        loadData();
    };

    const getPreviewLatex = (pattern: MathPattern) => {
        // Generate a preview by replacing placeholders with their names
        let preview = pattern.latex;

        // Strategy: Use marker to preserve double-brace placeholders
        // Step 1: Replace {{x}} with a marker + content (these NEED braces in final output)
        preview = preview.replace(/\{\{([^}]+)\}\}/g, '◆BRACE◆$1◆ENDBRACE◆');

        // Step 2: Protect LaTeX command arguments like \operatorname{tr} by marking them
        // Match \command{arg} where command is letters and arg doesn't contain markers
        preview = preview.replace(/\\([a-zA-Z]+)\{([^}◆]+)\}/g, '\\$1◆CMDARG◆$2◆ENDCMD◆');

        // Step 3: Replace remaining single {x} with just x (these are plain values, no braces needed)
        preview = preview.replace(/\{([^}◆]+)\}/g, '$1');

        // Step 4: Convert markers back to braces
        preview = preview.replace(/◆BRACE◆([^◆]+)◆ENDBRACE◆/g, '{$1}');
        preview = preview.replace(/◆CMDARG◆([^◆]+)◆ENDCMD◆/g, '{$1}');

        return preview;
    };

    const getTestResult = (testInput: string) => {
        if (!patternForm.pattern || !patternForm.latex || !testInput) return null;

        // Extract placeholder names from pattern (e.g., {A}inv -> ['A'])
        const placeholderRegex = /\{([^}]+)\}/g;
        const placeholders: string[] = [];
        let match;
        while ((match = placeholderRegex.exec(patternForm.pattern)) !== null) {
            placeholders.push(match[1]);
        }

        if (placeholders.length === 0) return null;

        // Escape regex special characters in the pattern, except for placeholders
        const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        // Split pattern by placeholders, escape each part, then rejoin with capture groups
        const parts = patternForm.pattern.split(/\{[^}]+\}/);
        const escapedParts = parts.map(part => escapeRegex(part));
        const patternRegex = escapedParts.join('(.+)');

        const testMatch = new RegExp('^' + patternRegex + '$').exec(testInput);

        if (!testMatch) return null;

        // Replace placeholders in latex with captured values
        let result = patternForm.latex;
        placeholders.forEach((placeholder, index) => {
            const captureValue = testMatch[index + 1] || '';
            // Replace both {placeholder} and {{placeholder}}
            result = result.split(`{${placeholder}}`).join(captureValue);
            result = result.split(`{{${placeholder}}}`).join(`{${captureValue}}`);
        });

        return result;
    };

    // Helper for inline testing any pattern (not just the form)
    const getInlineTestResult = (pattern: MathPattern, testInput: string): string | null => {
        if (!pattern.pattern || !pattern.latex || !testInput) return null;

        const placeholderRegex = /\{([^}]+)\}/g;
        const placeholders: string[] = [];
        let match;
        while ((match = placeholderRegex.exec(pattern.pattern)) !== null) {
            placeholders.push(match[1]);
        }

        if (placeholders.length === 0) return null;

        const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const parts = pattern.pattern.split(/\{[^}]+\}/);
        const escapedParts = parts.map(part => escapeRegex(part));
        const patternRegex = escapedParts.join('(.+)');

        const testMatch = new RegExp('^' + patternRegex + '$').exec(testInput);
        if (!testMatch) return null;

        let result = pattern.latex;
        placeholders.forEach((placeholder, index) => {
            const captureValue = testMatch[index + 1] || '';
            result = result.split(`{${placeholder}}`).join(captureValue);
            result = result.split(`{{${placeholder}}}`).join(`{${captureValue}}`);
        });

        return result;
    };

    const getPatternMismatchExplanation = (testInput: string) => {
        if (!patternForm.pattern || !testInput) return '';

        // Check if pattern has any placeholders
        const hasPlaceholders = /\{[^}]+\}/.test(patternForm.pattern);
        if (!hasPlaceholders) {
            return `Your pattern "${patternForm.pattern}" has no placeholders! Use {A}, {x}, etc. to mark the variable parts. Example: "{A}inv" or "{x}_bar"`;
        }

        // Get the expected pattern structure
        const cleanPattern = patternForm.pattern.replace(/[{}]/g, '');

        // If test input exactly matches the clean pattern, it's actually valid
        if (testInput === cleanPattern) {
            return `"${testInput}" matches! But check if your LaTeX uses the same placeholder names as your pattern.`;
        }

        // Extract fixed parts from pattern (non-placeholder parts)
        const fixedParts = patternForm.pattern.split(/\{[^}]+\}/).filter(p => p.length > 0);

        // Check which fixed parts are missing in the input
        const missingParts = fixedParts.filter(part => !testInput.includes(part));

        if (missingParts.length > 0) {
            return `Your input is missing: "${missingParts.join('", "')}". Expected format: ${cleanPattern}`;
        }

        // Check if the order is wrong
        if (fixedParts.length > 0) {
            const firstFixedPart = fixedParts[0];
            const patternStartsWithPlaceholder = patternForm.pattern.startsWith('{');

            if (patternStartsWithPlaceholder) {
                // Pattern like {A}inv - check if input ends with the fixed part
                if (!testInput.endsWith(firstFixedPart)) {
                    return `Input should end with "${firstFixedPart}". You typed: "${testInput}". Example: X${firstFixedPart}`;
                }
            } else {
                // Pattern like inv{A} - check if input starts with the fixed part
                if (!testInput.startsWith(firstFixedPart)) {
                    return `Input should start with "${firstFixedPart}". You typed: "${testInput}". Example: ${firstFixedPart}X`;
                }
            }
        }

        return `The input "${testInput}" doesn't match the pattern "${cleanPattern}". Check for typos.`;
    };

    const handleAiFillSymbol = async () => {
        if (!aiSymbolDesc.trim()) return;
        setAiSymbolLoading(true);
        setAiSymbolError('');
        try {
            const result = await suggestMathSymbol(aiSymbolDesc.trim());
            setSymbolForm({
                trigger: result.trigger.join(', '),
                unicode: result.unicode,
                latex: result.latex,
                description: result.description,
                category: result.category,
            });
        } catch (e: any) {
            setAiSymbolError('AI không thể xác định symbol này. Thử mô tả rõ hơn.');
        } finally {
            setAiSymbolLoading(false);
        }
    };

    const resetSymbolForm = () => {
        setSymbolForm({ trigger: '', unicode: '', latex: '', description: '', category: 'Custom' });
        setAiSymbolDesc('');
        setAiSymbolError('');
    };

    const resetPatternForm = () => {
        setPatternForm({ pattern: '', latex: '', description: '', priority: 10 });
    };

    const startEditSymbol = (symbol: MathSymbol) => {
        setEditingSymbol(symbol.id || '');
        setSymbolForm({
            trigger: symbol.trigger.join(', '),
            unicode: symbol.unicode,
            latex: symbol.latex,
            description: symbol.description,
            category: symbol.category
        });
    };

    const startEditPattern = (pattern: MathPattern) => {
        setEditingPattern(pattern.id || '');
        setPatternForm({
            pattern: pattern.pattern,
            latex: pattern.latex,
            description: pattern.description,
            priority: pattern.priority || 10
        });
        setIsAddingPattern(true);
    };

    const filteredSymbols = symbols.filter(s =>
        s.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.trigger.some(t => t.toLowerCase().includes(searchQuery.toLowerCase())) ||
        s.latex.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredPatterns = patterns.filter(p =>
        p.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.pattern.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Group symbols by category
    const symbolsByCategory = filteredSymbols.reduce((acc, symbol) => {
        const cat = symbol.category || 'Other';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(symbol);
        return acc;
    }, {} as Record<string, MathSymbol[]>);

    return (
        <>
        <div className="space-y-4">
            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-gray-100 dark:bg-slate-900 rounded-lg">
                <button
                    onClick={() => setActiveTab('symbols')}
                    className={`flex-1 px-4 py-2 rounded-md font-semibold text-sm transition-all ${activeTab === 'symbols'
                        ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                >
                    📚 Symbols ({symbols.length})
                </button>
                <button
                    onClick={() => setActiveTab('patterns')}
                    className={`flex-1 px-4 py-2 rounded-md font-semibold text-sm transition-all ${activeTab === 'patterns'
                        ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                >
                    🔤 Patterns ({patterns.length})
                </button>
            </div>

            {/* Search & Add */}
            <div className="flex gap-2">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                    />
                </div>
                <button
                    onClick={() => activeTab === 'symbols' ? setIsAddingSymbol(true) : setIsAddingPattern(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg shadow-indigo-500/30 font-semibold text-sm"
                >
                    <Plus size={18} />
                    Add
                </button>
            </div>

            {/* Symbols Tab */}
            {
                activeTab === 'symbols' && (
                    <div className="space-y-4">
                        {/* Add Symbol Form */}
                        {isAddingSymbol && (
                            <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-dashed border-indigo-300 dark:border-indigo-700 rounded-xl space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-bold text-indigo-900 dark:text-indigo-200">✨ New Symbol</h4>
                                    <button onClick={() => { setIsAddingSymbol(false); resetSymbolForm(); }} className="text-gray-500 hover:text-gray-700">
                                        <X size={20} />
                                    </button>
                                </div>

                                {/* AI Fill */}
                                <div className="space-y-2">
                                    <p className="text-xs font-semibold text-indigo-700 dark:text-indigo-300">Mô tả symbol bằng ngôn ngữ tự nhiên — AI tự điền hết</p>
                                    <div className="flex gap-2">
                                        <input
                                            placeholder='e.g. "capital epsilon calligraphic" hoặc "chữ E hoa kiểu viết tay"'
                                            value={aiSymbolDesc}
                                            onChange={(e) => setAiSymbolDesc(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAiFillSymbol()}
                                            className="flex-1 px-3 py-2 border border-indigo-300 dark:border-indigo-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 dark:bg-slate-700"
                                        />
                                        <button
                                            onClick={handleAiFillSymbol}
                                            disabled={aiSymbolLoading || !aiSymbolDesc.trim()}
                                            className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm transition-all"
                                        >
                                            {aiSymbolLoading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                                            AI Fill
                                        </button>
                                    </div>
                                    {aiSymbolError && <p className="text-xs text-red-500">{aiSymbolError}</p>}
                                </div>

                                <div className="border-t border-indigo-200 dark:border-indigo-700 pt-3">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Hoặc điền thủ công:</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <input placeholder="Triggers (e.g., lambda, LAMBDA)" value={symbolForm.trigger} onChange={(e) => setSymbolForm({ ...symbolForm, trigger: e.target.value })} className="col-span-2 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 dark:bg-slate-700 dark:border-slate-600" />
                                        <input placeholder="Unicode (e.g., λ)" value={symbolForm.unicode} onChange={(e) => setSymbolForm({ ...symbolForm, unicode: e.target.value })} className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 dark:bg-slate-700 dark:border-slate-600" />
                                        <input placeholder="LaTeX (e.g., \\lambda)" value={symbolForm.latex} onChange={(e) => setSymbolForm({ ...symbolForm, latex: e.target.value })} className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 dark:bg-slate-700 dark:border-slate-600" />
                                        <input placeholder="Description" value={symbolForm.description} onChange={(e) => setSymbolForm({ ...symbolForm, description: e.target.value })} className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 dark:bg-slate-700 dark:border-slate-600" />
                                        <input placeholder="Category" value={symbolForm.category} onChange={(e) => setSymbolForm({ ...symbolForm, category: e.target.value })} className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 dark:bg-slate-700 dark:border-slate-600" />
                                    </div>
                                </div>

                                <button onClick={handleAddSymbol} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold">
                                    <Save size={16} /> Save Symbol
                                </button>
                            </div>
                        )}

                        {/* Symbols Grid by Category */}
                        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                            {Object.entries(symbolsByCategory).map(([category, categorySymbols]) => (
                                <div key={category}>
                                    <div className="sticky top-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm py-2 mb-2 border-b border-gray-200 dark:border-slate-700 z-10">
                                        <h5 className="font-bold text-sm text-gray-700 dark:text-gray-300">{category}</h5>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {categorySymbols.map((symbol) => (
                                            <div
                                                key={symbol.id || symbol.unicode}
                                                className="group relative p-3 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-600 transition-all"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-3xl mb-1">{symbol.unicode}</div>
                                                        <div className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">{symbol.description}</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">{symbol.latex}</div>
                                                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">{symbol.trigger.slice(0, 2).join(', ')}{symbol.trigger.length > 2 && '...'}</div>
                                                    </div>
                                                    {symbol.isCustom && (
                                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <button onClick={() => startEditSymbol(symbol)} className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded">
                                                                <Edit2 size={14} />
                                                            </button>
                                                            <button onClick={() => handleDeleteSymbol(symbol.id!)} className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                {symbol.isCustom && <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded-full font-bold">Custom</div>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            }

            {/* Patterns Tab */}
            {
                activeTab === 'patterns' && (
                    <div className="space-y-4">
                        {/* Live Demo Panel */}
                        <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl space-y-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <span className="font-bold text-indigo-900 dark:text-indigo-200 text-sm">💡 Demo: Gõ tắt → Toán học đẹp</span>
                                    <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-0.5">
                                        {demoMode === 'edit'
                                            ? 'Edit mode: thấy cú pháp. Bấm "Xem" để thấy kết quả render.'
                                            : 'View mode: kết quả render — bật/tắt pattern bên dưới rồi bấm lại "Xem" để thấy thay đổi.'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <button
                                        onClick={() => { setDemoText(DEMO_TEXT); setDemoMode('edit'); }}
                                        title="Khôi phục demo gốc"
                                        className="p-1.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded transition-colors"
                                    >
                                        <RotateCcw size={13} />
                                    </button>
                                    <button
                                        onClick={() => setPlaygroundOpen(true)}
                                        title="Mở toàn màn hình"
                                        className="p-1.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded transition-colors"
                                    >
                                        <Maximize2 size={13} />
                                    </button>
                                    <div className="flex rounded-lg overflow-hidden border border-indigo-300 dark:border-indigo-700 text-xs font-semibold">
                                        <button
                                            onClick={() => setDemoMode('edit')}
                                            className={`px-3 py-1.5 transition-colors ${demoMode === 'edit' ? 'bg-indigo-600 text-white' : 'text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/40'}`}
                                        >
                                            ✍️ Gõ
                                        </button>
                                        <button
                                            onClick={() => setDemoMode('view')}
                                            className={`px-3 py-1.5 transition-colors ${demoMode === 'view' ? 'bg-indigo-600 text-white' : 'text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/40'}`}
                                        >
                                            👁 Xem
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {demoMode === 'edit' ? (
                                <textarea
                                    value={demoText}
                                    onChange={e => setDemoText(e.target.value)}
                                    spellCheck={false}
                                    className="w-full h-52 px-3 py-2.5 rounded-lg border border-indigo-200 dark:border-indigo-700 bg-white dark:bg-slate-800 text-sm font-mono text-gray-800 dark:text-gray-200 resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none leading-relaxed"
                                />
                            ) : (
                                <div
                                    className="w-full h-52 px-3 py-2.5 rounded-lg border border-indigo-200 dark:border-indigo-700 bg-white dark:bg-slate-800 text-sm text-gray-800 dark:text-gray-200 overflow-y-auto leading-relaxed
                                        [&>h1]:text-xl [&>h1]:font-bold [&>h1]:mt-2 [&>h1]:mb-1
                                        [&>h2]:text-base [&>h2]:font-bold [&>h2]:mt-2 [&>h2]:mb-1 [&>h2]:text-indigo-700 [&>h2]:dark:text-indigo-300
                                        [&>h3]:text-sm [&>h3]:font-semibold [&>h3]:mt-1.5 [&>h3]:mb-0.5"
                                    dangerouslySetInnerHTML={{ __html: demoRendered }}
                                />
                            )}
                        </div>

                        {/* Bulk Actions */}
                        <div className="flex items-center gap-2 justify-end">
                            {patterns.some(p => p.isActive !== false) ? (
                                <button
                                    onClick={() => { disableAllPatterns(); loadData(); }}
                                    className="px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                                >
                                    🚫 Disable All
                                </button>
                            ) : (
                                <button
                                    onClick={() => { enableAllPatterns(); loadData(); }}
                                    className="px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors"
                                >
                                    ✅ Enable All
                                </button>
                            )}
                        </div>

                        {/* Add Pattern Form */}
                        {isAddingPattern && (
                            <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-dashed border-purple-300 dark:border-purple-700 rounded-xl space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-bold text-purple-900 dark:text-purple-200">✨ New Pattern</h4>
                                    <button onClick={() => { setIsAddingPattern(false); resetPatternForm(); }} className="text-gray-500 hover:text-gray-700">
                                        <X size={20} />
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <input placeholder="Pattern (e.g., {A}inv)" value={patternForm.pattern} onChange={(e) => setPatternForm({ ...patternForm, pattern: e.target.value })} className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 dark:bg-slate-700 dark:border-slate-600" />
                                    <input placeholder="LaTeX (e.g., {A}^{-1})" value={patternForm.latex} onChange={(e) => setPatternForm({ ...patternForm, latex: e.target.value })} className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 dark:bg-slate-700 dark:border-slate-600" />
                                    <input placeholder="Description" value={patternForm.description} onChange={(e) => setPatternForm({ ...patternForm, description: e.target.value })} className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 dark:bg-slate-700 dark:border-slate-600" />
                                    <input type="number" placeholder="Priority (higher = first)" value={patternForm.priority} onChange={(e) => setPatternForm({ ...patternForm, priority: parseInt(e.target.value) })} className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 dark:bg-slate-700 dark:border-slate-600" />
                                </div>

                                {/* Dynamic Helper Text */}
                                {patternForm.pattern && patternForm.latex && (
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2 p-3 bg-white dark:bg-slate-800 rounded-lg border border-purple-200 dark:border-purple-800/50 text-sm animate-in fade-in slide-in-from-top-2 duration-200">
                                            <div className="text-purple-500">
                                                <Search size={16} />
                                            </div>
                                            <div className="flex-1 flex flex-wrap items-center gap-2 text-gray-600 dark:text-gray-300">
                                                <span>Type</span>
                                                <code className="px-1.5 py-0.5 bg-gray-100 dark:bg-slate-700 rounded text-purple-600 dark:text-purple-400 font-mono font-bold border border-gray-200 dark:border-slate-600">
                                                    {patternForm.pattern.replace(/[{}]/g, '')}
                                                </code>
                                                <span>to see:</span>
                                                <div className="px-2 py-0.5 bg-gray-50 dark:bg-slate-700 rounded border border-gray-200 dark:border-slate-600 min-w-[30px] flex items-center justify-center">
                                                    <LatexPreview latex={getPreviewLatex({ ...patternForm, id: 'preview', isActive: true, isCustom: true } as any)} />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Interactive Test Area */}
                                        <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800/50">
                                            <div className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-2">🧪 Test It Out</div>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="text"
                                                    placeholder={`Try: ${patternForm.pattern.replace(/\{([^}]+)\}/g, (_, p) => p.toUpperCase())}...`}
                                                    value={patternTestInput}
                                                    onChange={(e) => setPatternTestInput(e.target.value)}
                                                    className="flex-1 px-3 py-2 border border-blue-300 dark:border-blue-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:bg-slate-800"
                                                />
                                                <span className="text-gray-400">→</span>
                                                {/* Always show output box with 3 states */}
                                                {!patternTestInput ? (
                                                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 text-gray-400 text-sm min-w-[100px]">
                                                        <span className="italic">Type to test...</span>
                                                    </div>
                                                ) : getTestResult(patternTestInput) ? (
                                                    <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-300 dark:border-green-700 min-w-[100px]">
                                                        <span className="text-green-600 dark:text-green-400">✓</span>
                                                        <div className="px-2 py-0.5 bg-white dark:bg-slate-700 rounded border border-green-200 dark:border-green-600 min-w-[30px] flex items-center justify-center">
                                                            <LatexPreview latex={getTestResult(patternTestInput)!} />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 text-sm min-w-[100px]">
                                                            <span>⚠️</span>
                                                            <span className="text-xs">No match</span>
                                                            <button
                                                                onClick={() => setShowMismatchExplanation(!showMismatchExplanation)}
                                                                className="text-xs underline hover:text-amber-900 dark:hover:text-amber-200 transition-colors ml-1"
                                                            >
                                                                {showMismatchExplanation ? 'Hide' : 'Why?'}
                                                            </button>
                                                        </div>
                                                        {showMismatchExplanation && (
                                                            <div className="px-3 py-2 bg-amber-50/50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300 animate-in fade-in slide-in-from-top-1 duration-200">
                                                                💡 {getPatternMismatchExplanation(patternTestInput)}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <button onClick={handleAddPattern} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold">
                                    <Save size={16} /> Save Pattern
                                </button>
                            </div>
                        )}

                        {/* Patterns List */}
                        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                            {filteredPatterns.map((pattern) => {
                                const isActive = pattern.isActive !== false;
                                return (
                                    <div key={pattern.id || pattern.pattern} className={`group p-4 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg hover:shadow-md transition-all ${!isActive ? 'opacity-60 grayscale-[0.5]' : 'hover:border-purple-300 dark:hover:border-purple-600'}`}>
                                        <div className="flex items-center justify-between gap-4">
                                            {/* Left: Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                    <span className="font-bold text-gray-900 dark:text-gray-100">{pattern.description}</span>
                                                    {pattern.isCustom && <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded-full font-bold">Custom</span>}
                                                    {!pattern.isCustom && <span className="px-2 py-0.5 bg-gray-100 dark:bg-slate-600 text-gray-500 dark:text-gray-400 text-xs rounded-full">Default</span>}
                                                    {pattern.mathZoneOnly && (
                                                        <span title="Chỉ hoạt động trong $$ ... $$ — tránh trigger nhầm trong văn bản thường" className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs rounded-full font-mono font-bold cursor-help">
                                                            ⚠️ $$
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="text-xs text-gray-400 dark:text-gray-500">Gõ:</span>
                                                    <code className={`px-2 py-1 rounded font-mono text-sm ${pattern.mathZoneOnly ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700' : 'bg-gray-100 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400'}`}>
                                                        {getExampleInput(pattern)}
                                                    </code>
                                                    <span className="text-gray-400">→</span>
                                                    <div className="px-3 py-1 bg-gray-50 dark:bg-slate-800 rounded border border-gray-100 dark:border-slate-600 min-w-[60px] flex items-center justify-center">
                                                        <LatexPreview latex={getPreviewLatex(pattern)} />
                                                    </div>
                                                    <code className="text-xs text-gray-400 dark:text-gray-500 font-mono hidden sm:inline-block max-w-[150px] truncate" title={pattern.latex}>{pattern.latex}</code>
                                                </div>
                                            </div>

                                            {/* Right: Actions */}
                                            {/* Right: Actions */}
                                            <div className="flex items-center gap-2">
                                                {/* Try Button */}
                                                <button
                                                    onClick={() => {
                                                        if (testingPatternId === (pattern.id || pattern.pattern)) {
                                                            setTestingPatternId(null);
                                                            setInlineTestInput('');
                                                        } else {
                                                            setTestingPatternId(pattern.id || pattern.pattern);
                                                            setInlineTestInput('');
                                                        }
                                                    }}
                                                    className={`p-2 rounded transition-colors ${testingPatternId === (pattern.id || pattern.pattern) ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}
                                                    title="Thử pattern này"
                                                >
                                                    <FlaskConical size={16} />
                                                </button>

                                                {pattern.isCustom && (
                                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button onClick={() => startEditPattern(pattern)} className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded">
                                                            <Edit2 size={16} />
                                                        </button>
                                                        <button onClick={() => handleDeletePattern(pattern.id!)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Toggle Switch */}
                                                <button
                                                    onClick={() => handleTogglePattern(pattern)}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${isActive ? 'bg-purple-600' : 'bg-gray-200 dark:bg-slate-600'} cursor-pointer`}
                                                    title={isActive ? "Click to disable" : "Click to enable"}
                                                >
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Inline Test Area */}
                                        {testingPatternId === (pattern.id || pattern.pattern) && (
                                            <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800/50 animate-in slide-in-from-top-2 duration-200">
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="text"
                                                        placeholder={`Try: ${pattern.pattern.replace(/\{([^}]+)\}/g, (_, p) => p.toUpperCase())}...`}
                                                        value={inlineTestInput}
                                                        onChange={(e) => setInlineTestInput(e.target.value)}
                                                        autoFocus
                                                        className="flex-1 px-3 py-2 border border-blue-300 dark:border-blue-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:bg-slate-800"
                                                    />
                                                    <span className="text-gray-400">→</span>
                                                    {!inlineTestInput ? (
                                                        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 text-gray-400 text-sm min-w-[80px]">
                                                            <span className="italic text-xs">Type...</span>
                                                        </div>
                                                    ) : getInlineTestResult(pattern, inlineTestInput) ? (
                                                        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-300 dark:border-green-700 min-w-[80px]">
                                                            <span className="text-green-600 dark:text-green-400">✓</span>
                                                            <div className="px-2 py-0.5 bg-white dark:bg-slate-700 rounded border border-green-200 dark:border-green-600 min-w-[30px] flex items-center justify-center">
                                                                <LatexPreview latex={getInlineTestResult(pattern, inlineTestInput)!} />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 text-sm min-w-[80px]">
                                                            <span>⚠️</span>
                                                            <span className="text-xs">No match</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )
            }
        </div>

        {/* ── Pattern Playground: full-screen two-column overlay ── */}
        {playgroundOpen && (
            <div className="fixed inset-0 z-[600] bg-white dark:bg-slate-900 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-slate-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <span className="text-base font-bold text-gray-900 dark:text-gray-100">⚡ Pattern Playground</span>
                        <span className="text-sm text-gray-400 dark:text-gray-500 hidden sm:block">Bật/tắt pattern bên trái → kết quả cập nhật ngay bên phải</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {patterns.some(p => p.isActive !== false) ? (
                            <button onClick={() => { disableAllPatterns(); loadData(); }} className="px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg hover:bg-amber-100 transition-colors">🚫 Tắt tất cả</button>
                        ) : (
                            <button onClick={() => { enableAllPatterns(); loadData(); }} className="px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg hover:bg-green-100 transition-colors">✅ Bật tất cả</button>
                        )}
                        <button onClick={() => setPlaygroundOpen(false)} className="p-2 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Body: search bar + table */}
                <div className="flex flex-col flex-1 overflow-hidden">

                    {/* Search bar */}
                    <div className="px-6 py-3 border-b border-gray-200 dark:border-slate-700 shrink-0 bg-gray-50 dark:bg-slate-800/50">
                        <div className="relative max-w-sm">
                            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                placeholder="Tìm pattern..."
                                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500 outline-none"
                            />
                        </div>
                    </div>

                    {/* Table header */}
                    <div className="grid grid-cols-[300px_1fr_1fr] border-b border-gray-200 dark:border-slate-700 shrink-0 bg-gray-100/80 dark:bg-slate-800/80 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        <div className="px-6 py-2.5">Pattern</div>
                        <div className="px-6 py-2.5 border-l border-gray-200 dark:border-slate-700">✍️ Gõ (Input)</div>
                        <div className="px-6 py-2.5 border-l border-gray-200 dark:border-slate-700">👁 Xem (Output)</div>
                    </div>

                    {/* Scrollable rows — one per pattern */}
                    <div className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-slate-700/60">
                        {filteredPatterns.map(pattern => {
                            const isActive = pattern.isActive !== false;
                            const exInput = getExampleInput(pattern);
                            return (
                                <div
                                    key={pattern.id || pattern.pattern}
                                    className={`grid grid-cols-[300px_1fr_1fr] items-center min-h-[52px] transition-colors ${isActive ? 'bg-white dark:bg-slate-900' : 'bg-gray-50/60 dark:bg-slate-800/30'} hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10`}
                                >
                                    {/* Col 1: toggle + pattern name */}
                                    <div
                                        className="flex items-center gap-3 px-6 py-3 cursor-pointer select-none"
                                        onClick={() => handleTogglePattern(pattern)}
                                    >
                                        <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${isActive ? 'bg-purple-600' : 'bg-gray-200 dark:bg-slate-600'}`}>
                                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${isActive ? 'translate-x-4' : 'translate-x-0.5'}`} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className={`text-sm font-medium truncate ${isActive ? 'text-gray-800 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'}`}>{pattern.description}</span>
                                                {pattern.mathZoneOnly && <span className="px-1 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs rounded font-mono leading-none whitespace-nowrap">⚠️$$</span>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Col 2: example input (what to type) */}
                                    <div className={`px-6 py-3 border-l border-gray-100 dark:border-slate-700/60 ${isActive ? '' : 'opacity-50'}`}>
                                        <code className="text-sm font-mono text-indigo-700 dark:text-indigo-300">{exInput}</code>
                                        {pattern.example && (
                                            <div className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 font-mono leading-snug">{pattern.example}</div>
                                        )}
                                    </div>

                                    {/* Col 3: rendered output */}
                                    <div className={`px-6 py-3 border-l border-gray-100 dark:border-slate-700/60 ${isActive ? '' : 'opacity-50'}`}>
                                        <div
                                            className="text-sm text-gray-800 dark:text-gray-200"
                                            dangerouslySetInnerHTML={{ __html: isActive ? parseMarkdown(exInput) : exInput }}
                                        />
                                        {pattern.example && (
                                            <div
                                                className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 leading-snug"
                                                dangerouslySetInnerHTML={{ __html: isActive ? parseMarkdown(pattern.example) : pattern.example }}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        )}
        </>
    );
};
